import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import duration from "dayjs/plugin/duration";
import isoWeek from "dayjs/plugin/isoWeek";
import isTomorrow from "dayjs/plugin/isTomorrow";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isoWeek);
dayjs.extend(isTomorrow);
dayjs.extend(timezone);
dayjs.extend(utc);

export const time = dayjs;
time.utc = dayjs.utc;
export const timeAmount = dayjs.duration;

const HUMAN_DURATION_PATTERN =
  /^\s*(\d+(?:\.\d+)?)\s*(milliseconds?|msecs?|ms|seconds?|secs?|s|minutes?|mins?|m|hours?|hrs?|h|days?|d|weeks?|w)\s*$/i;
const HUMAN_DURATION_MULTIPLIERS = {
  ms: 1,
  msec: 1,
  msecs: 1,
  millisecond: 1,
  milliseconds: 1,
  s: 1000,
  sec: 1000,
  secs: 1000,
  second: 1000,
  seconds: 1000,
  m: 60_000,
  min: 60_000,
  mins: 60_000,
  minute: 60_000,
  minutes: 60_000,
  h: 3_600_000,
  hr: 3_600_000,
  hrs: 3_600_000,
  hour: 3_600_000,
  hours: 3_600_000,
  d: 86_400_000,
  day: 86_400_000,
  days: 86_400_000,
  w: 604_800_000,
  week: 604_800_000,
  weeks: 604_800_000,
} as const;

/**
 * Create a Dayjs instance for a given input.
 *
 * Prefer this over calling `time()` directly when you want a semantic "date"
 * constructor that works for both Date and string inputs.
 */
export function date(input?: string | number | Date | Dayjs | undefined | null): Dayjs {
  if (input == null) {
    return time();
  }

  return time.isDayjs(input) ? input : time(input);
}

/**
 * Parse a date string into a Dayjs instance.
 *
 * Defaults to strict parsing of "YYYY-MM-DD".
 */
export function parseDate(value: string, format = "YYYY-MM-DD"): Dayjs {
  // With customParseFormat, the third arg is `strict` when no locale is provided.
  return time(value, format, true);
}

/**
 * Formats FHIR's date-only Patient.birthDate without applying a timezone.
 */
export function formatDateOfBirth(birthDate: string | undefined): string {
  if (!birthDate) return "";

  const parsedDateOfBirth = time.utc(birthDate, "YYYY-MM-DD", true);
  if (!parsedDateOfBirth.isValid()) return "";

  // "Mar 11, 1984" — matches the patient list DOB column.
  return parsedDateOfBirth.format("MMM D, YYYY");
}

/**
 * Validates IANA timezone identifiers before using them in date math.
 */
export function isValidTimezone(timezone: string): boolean {
  try {
    if (typeof Intl.supportedValuesOf === "function") {
      return Intl.supportedValuesOf("timeZone").includes(timezone);
    }

    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Format a date-like input with a given Dayjs format string.
 *
 * Returns an empty string for null/undefined/invalid inputs.
 */
export function formatDate(
  input: string | number | Date | Dayjs | undefined | null,
  format: string,
): string {
  if (input == null) {
    return "";
  }

  const parsed = time.isDayjs(input) ? input : time(input);
  if (!parsed.isValid()) {
    return "";
  }

  return parsed.format(format);
}

/**
 * Formats a date-like input into a short human label.
 *
 * - Today → "Today"
 * - Tomorrow → "Tomorrow"
 * - Yesterday → "Yesterday"
 * - Same calendar year → "MMM D" (e.g. "Sep 12")
 * - Different year → "MMM D, YYYY" (e.g. "Sep 12, 2024")
 */
export function formatRelativeDateLabel(
  input: string | number | Date | Dayjs | undefined | null,
): string {
  if (!input) return "";
  const date = time.isDayjs(input) ? input : time(input as string | number | Date);
  if (!date.isValid()) return "";

  const today = time();
  const tomorrow = today.add(1, "day");
  const yesterday = today.subtract(1, "day");

  if (date.isSame(today, "day")) return "Today";
  if (date.isSame(tomorrow, "day")) return "Tomorrow";
  if (date.isSame(yesterday, "day")) return "Yesterday";
  if (date.isSame(today, "year")) return date.format("MMM D");
  return date.format("MMM D, YYYY");
}

/**
 * Formats a human readable duration into milliseconds.
 * For example "1s" -> 1000
 * Often used with inngest
 */
export function parseHumanReadableDuration(durationStr: string): number | null {
  const match = HUMAN_DURATION_PATTERN.exec(durationStr);
  if (!match) {
    return null;
  }

  const [, rawValue, rawUnit] = match;
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return null;
  }

  const unit = rawUnit.toLowerCase() as keyof typeof HUMAN_DURATION_MULTIPLIERS;
  const multiplier = HUMAN_DURATION_MULTIPLIERS[unit];
  if (!multiplier) {
    return null;
  }

  return value * multiplier;
}

/**
 * Formats message time with relative date logic:
 * - Current day: shows time (e.g., "2:30 PM")
 * - Yesterday: shows "Yesterday"
 * - Last 6 days: shows day name (e.g., "Monday")
 * - Older: shows date (M/D/YY)
 */
export function formatMessageTime(dateString: string): string {
  const date = time(dateString);
  const today = time();

  if (date.isSame(today, "day")) {
    return date.format("h:mm A");
  }

  if (date.isSame(today.subtract(1, "day"), "day")) {
    return "Yesterday";
  }

  // Check if within last 6 complete days
  const sixDaysAgo = today.subtract(6, "day");
  if (date.isAfter(sixDaysAgo)) {
    return date.format("dddd"); // Full day name (Monday, Tuesday, etc.)
  }

  // Older than 6 days: show date
  return date.format("M/D/YY");
}

/**
 * Formats a date/time in a specific timezone with both abbreviation and UTC offset.
 * Falls back to UTC with explicit "UTC" indicator when no timezone is provided.
 *
 * @param date - The date/time to format (ISO string, Date, or Dayjs object)
 * @param timezone - IANA timezone ID (e.g., "America/New_York"). If not provided, defaults to UTC.
 * @returns Formatted date string with timezone indicator
 *
 * @example
 * // With timezone: "December 23, 2024 10:45 AM EST (UTC-5)"
 * formatDateTimeWithTimezone("2024-12-23T15:45:00Z", "America/New_York")
 *
 * // Without timezone (UTC fallback): "December 23, 2024 3:45 PM UTC"
 * formatDateTimeWithTimezone("2024-12-23T15:45:00Z")
 */
export function formatDateTimeWithTimezone(
  date: string | Date | Dayjs | undefined,
  timezone?: string,
): string {
  if (!date) return "";

  const base = time.isDayjs(date) ? date : time(date);
  if (!base.isValid()) return "";

  if (timezone) {
    try {
      const tz = base.tz(timezone);
      const abbrev = tz.format("z"); // EST, PST, EDT, etc.
      const offset = tz.format("Z"); // -05:00, -08:00, etc.
      // Convert -05:00 to -5, +00:00 to +0, etc.
      const offsetHours = offset.replace(/:00$/, "").replace(/([+-])0/, "$1");
      return `${tz.format("MMMM D, YYYY h:mm A")} ${abbrev} (UTC${offsetHours})`;
    } catch {
      // Invalid timezone - fall through to UTC fallback
    }
  }

  // Fallback: UTC with explicit indicator
  return `${base.utc().format("MMMM D, YYYY h:mm A")} UTC`;
}
